/**
 * Dynamic Translation Service
 * Works like Google Translate - translates text on-the-fly using API
 * No hard-coded translation files needed
 */

export type LanguageCode = string; // e.g., 'en', 'fr', 'rw', 'sw', 'es', etc.

export interface TranslationCache {
  [key: string]: {
    [lang: string]: string;
  };
}

class TranslationService {
  private cache: TranslationCache = {};
  private currentLanguage: LanguageCode = 'en';
  private apiKey: string | null = null;
  private apiUrl: string = 'https://api.mymemory.translated.net/get'; // Free translation API
  private useGoogleTranslate: boolean = false; // Can switch to Google Translate API if needed
  private rateLimited: boolean = false;
  private rateLimitUntil: number = 0; // Timestamp when rate limit expires
  private pendingRequests: Set<string> = new Set(); // Track pending requests to prevent duplicates

  /**
   * Initialize the translation service
   */
  init(language: LanguageCode = 'en', apiKey?: string) {
    this.currentLanguage = language;
    this.apiKey = apiKey || null;
    
    // Load cached translations from localStorage
    this.loadCache();
    
    // Load rate limit state from localStorage
    this.loadRateLimitState();
    
    // Detect browser language if not set
    if (!localStorage.getItem('app_language')) {
      const browserLang = navigator.language.split('-')[0];
      this.setLanguage(browserLang);
    }
  }

  /**
   * Set the current language
   */
  setLanguage(language: LanguageCode) {
    const previousLanguage = this.currentLanguage;
    this.currentLanguage = language;
    localStorage.setItem('app_language', language);
    
    // If switching to French, clear any potentially corrupted French cache entries
    // This ensures fresh translations when switching to French
    if (language === 'fr' && previousLanguage !== 'fr') {
      this.clearLanguageCache('fr');
    }
  }
  
  /**
   * Clear cache entries for a specific language
   */
  clearLanguageCache(language: LanguageCode) {
    let clearedCount = 0;
    for (const [key, translations] of Object.entries(this.cache)) {
      if (translations[language]) {
        delete translations[language];
        clearedCount++;
        // If no translations left for this key, remove the key entirely
        if (Object.keys(translations).length === 0) {
          delete this.cache[key];
        }
      }
    }
    if (clearedCount > 0) {
      this.saveCache();
      console.log(`Cleared ${clearedCount} cache entries for language: ${language}`);
    }
  }

  /**
   * Get current language
   */
  getLanguage(): LanguageCode {
    return this.currentLanguage || localStorage.getItem('app_language') || 'en';
  }

  /**
   * Check if text contains MyMemory warning messages
   */
  private containsWarning(text: string): boolean {
    if (!text) return false;
    return text.includes('MYMEMORY WARNING') || 
           text.includes('YOU USED ALL AVAILABLE FREE TRANSLATIONS') ||
           text.includes('NEXT AVAILABLE IN') ||
           text.includes('VISIT HTTPS://MYMEMORY.TRANSLATED.NET');
  }

  /**
   * Translate text dynamically
   */
  async translate(text: string, targetLang?: LanguageCode): Promise<string> {
    if (!text || !text.trim()) return text;
    
    const target = targetLang || this.currentLanguage;
    
    // If already in target language, return as-is
    if (target === 'en' && this.isEnglish(text)) {
      return text;
    }
    
    // IMPORTANT: Check rate limit FIRST, before any other operations
    // This prevents making API calls when rate limited
    if (this.isRateLimited()) {
      // Return original text if rate limited (don't make API call)
      return text;
    }
    
    // Check cache second
    const cacheKey = this.getCacheKey(text);
    const cached = this.cache[cacheKey]?.[target];
    if (cached) {
      // Filter out warnings from cached translations
      if (this.containsWarning(cached)) {
        // Remove the bad cache entry
        delete this.cache[cacheKey][target];
        if (Object.keys(this.cache[cacheKey]).length === 0) {
          delete this.cache[cacheKey];
        }
        this.saveCache();
        // Return original text instead of warning
        return text;
      }
      // For French, double-check that the cached translation is actually different from the original
      // This helps catch cases where the cache might have the original English text stored as French
      if (target === 'fr' && cached === text) {
        // If cached "French" translation is the same as English, it's likely invalid
        // Remove it and fetch a fresh translation
        delete this.cache[cacheKey][target];
        if (Object.keys(this.cache[cacheKey]).length === 0) {
          delete this.cache[cacheKey];
        }
        this.saveCache();
      } else {
        return cached;
      }
    }
    
    // Create a unique request key to prevent duplicate simultaneous requests
    const requestKey = `${cacheKey}:${target}`;
    if (this.pendingRequests.has(requestKey)) {
      // Request already in progress, wait a bit and return original text
      // This prevents duplicate API calls
      return text;
    }
    
    // Translate using API
    this.pendingRequests.add(requestKey);
    try {
      const translated = await this.fetchTranslation(text, 'en', target);
      
      // If translation contains warning or is original text (rate limited), don't cache
      if (this.containsWarning(translated) || translated === text) {
        return text;
      }
      
      // Only cache valid translations
      if (translated && translated !== text) {
        if (!this.cache[cacheKey]) {
          this.cache[cacheKey] = {};
        }
        this.cache[cacheKey][target] = translated;
        this.saveCache();
      }
      
      return translated;
    } catch (error) {
      // Don't log every failed translation to avoid console spam
      // Only log if it's not a rate limit issue
      if (!this.isRateLimited()) {
        console.warn('Translation failed, returning original text:', error);
      }
      return text; // Fallback to original text
    } finally {
      // Remove from pending requests
      this.pendingRequests.delete(requestKey);
    }
  }

  /**
   * Translate multiple texts at once (batch translation)
   */
  async translateBatch(texts: string[], targetLang?: LanguageCode): Promise<string[]> {
    const target = targetLang || this.currentLanguage;
    
    // Check cache for all texts first
    const results: string[] = [];
    const toTranslate: { index: number; text: string }[] = [];
    
    texts.forEach((text, index) => {
      const cacheKey = this.getCacheKey(text);
      if (this.cache[cacheKey]?.[target]) {
        results[index] = this.cache[cacheKey][target];
      } else {
        toTranslate.push({ index, text });
      }
    });
    
    // Translate remaining texts
    if (toTranslate.length > 0) {
      const translations = await Promise.all(
        toTranslate.map(({ text }) => this.translate(text, target))
      );
      
      toTranslate.forEach(({ index }, i) => {
        results[index] = translations[i];
      });
    }
    
    return results;
  }

  /**
   * Check if we're currently rate limited
   */
  private isRateLimited(): boolean {
    // Check in-memory state first
    if (this.rateLimited && Date.now() < this.rateLimitUntil) {
      return true;
    }
    
    // Check localStorage for persisted rate limit state
    try {
      const rateLimitData = localStorage.getItem('translation_rate_limit');
      if (rateLimitData) {
        const { until } = JSON.parse(rateLimitData);
        if (Date.now() < until) {
          // Still rate limited, update in-memory state
          this.rateLimited = true;
          this.rateLimitUntil = until;
          return true;
        } else {
          // Rate limit expired, clear it
          localStorage.removeItem('translation_rate_limit');
        }
      }
    } catch (error) {
      // Ignore errors reading from localStorage
    }
    
    // Reset rate limit flag if time has passed
    if (this.rateLimited && Date.now() >= this.rateLimitUntil) {
      this.rateLimited = false;
      this.rateLimitUntil = 0;
      localStorage.removeItem('translation_rate_limit');
    }
    return false;
  }

  /**
   * Load rate limit state from localStorage
   */
  private loadRateLimitState() {
    try {
      const rateLimitData = localStorage.getItem('translation_rate_limit');
      if (rateLimitData) {
        const { until } = JSON.parse(rateLimitData);
        if (Date.now() < until) {
          this.rateLimited = true;
          this.rateLimitUntil = until;
          const minutesRemaining = Math.round((until - Date.now()) / 60000);
          console.info(`Translation API rate limited. ${minutesRemaining} minutes remaining.`);
        } else {
          // Rate limit expired, clear it
          localStorage.removeItem('translation_rate_limit');
        }
      }
    } catch (error) {
      // Ignore errors
    }
  }

  /**
   * Save rate limit state to localStorage
   */
  private saveRateLimitState() {
    try {
      if (this.rateLimited && this.rateLimitUntil > 0) {
        localStorage.setItem('translation_rate_limit', JSON.stringify({
          until: this.rateLimitUntil
        }));
      } else {
        localStorage.removeItem('translation_rate_limit');
      }
    } catch (error) {
      // Ignore errors
    }
  }

  /**
   * Set rate limit (stop making API calls for a period)
   */
  private setRateLimit(durationMs: number = 3600000) { // Default 1 hour
    this.rateLimited = true;
    this.rateLimitUntil = Date.now() + durationMs;
    this.saveRateLimitState();
    const minutesRemaining = Math.round(durationMs / 60000);
    console.warn(`Translation API rate limited. Will retry after ${minutesRemaining} minutes. Using cached translations or English.`);
  }

  /**
   * Fetch translation from API
   */
  private async fetchTranslation(text: string, sourceLang: string, targetLang: string): Promise<string> {
    if (sourceLang === targetLang) return text;
    
    // Check if we're rate limited BEFORE making API call
    if (this.isRateLimited()) {
      // Don't log every time to avoid console spam
      return text;
    }
    
    // Use MyMemory Translation API (free, no API key needed)
    const url = `${this.apiUrl}?q=${encodeURIComponent(text)}&langpair=${sourceLang}|${targetLang}`;
    
    try {
      const response = await fetch(url);
      
      // Handle rate limiting (429 Too Many Requests)
      if (response.status === 429) {
        // Set rate limit for 1 hour (3600000 ms)
        this.setRateLimit(3600000);
        return text;
      }
      
      // Handle other errors
      if (!response.ok) {
        throw new Error(`Translation API returned status ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.responseData && data.responseData.translatedText) {
        let translatedText = data.responseData.translatedText;
        
        // Filter out MyMemory API warning messages
        if (this.containsWarning(translatedText)) {
          // API limit reached - set rate limit and return original text
          this.setRateLimit(3600000); // 1 hour
          return text;
        }
        
        return translatedText;
      }
      
      throw new Error('Translation API returned invalid response');
    } catch (error: any) {
      // If it's a network error and we got 429, we already handled it
      if (error.message?.includes('429') || error.message?.includes('Too Many Requests')) {
        this.setRateLimit(3600000);
        return text;
      }
      
      // Fallback: try Google Translate API if configured
      if (this.useGoogleTranslate && this.apiKey) {
        return this.fetchGoogleTranslation(text, sourceLang, targetLang);
      }
      throw error;
    }
  }

  /**
   * Fetch translation from Google Translate API (if API key is provided)
   */
  private async fetchGoogleTranslation(text: string, sourceLang: string, targetLang: string): Promise<string> {
    if (!this.apiKey) throw new Error('Google Translate API key not configured');
    
    const url = `https://translation.googleapis.com/language/translate/v2?key=${this.apiKey}`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        q: text,
        source: sourceLang,
        target: targetLang,
      }),
    });
    
    const data = await response.json();
    
    if (data.data && data.data.translations && data.data.translations[0]) {
      return data.data.translations[0].translatedText;
    }
    
    throw new Error('Google Translate API returned invalid response');
  }

  /**
   * Get cache key for text
   */
  private getCacheKey(text: string): string {
    return text.toLowerCase().trim();
  }

  /**
   * Check if text is already in English (simple heuristic)
   */
  private isEnglish(text: string): boolean {
    // Simple check - can be enhanced
    return /^[a-zA-Z0-9\s.,!?;:'"()\-]+$/.test(text);
  }

  /**
   * Load cache from localStorage
   */
  private loadCache() {
    try {
      const cached = localStorage.getItem('translation_cache');
      if (cached) {
        const parsedCache = JSON.parse(cached);
        // Filter out any cached entries that contain warnings
        const cleanedCache: TranslationCache = {};
        let hasWarnings = false;
        let validEntries = 0;
        
        for (const [key, translations] of Object.entries(parsedCache)) {
          const cleanedTranslations: { [lang: string]: string } = {};
          for (const [lang, text] of Object.entries(translations as { [lang: string]: string })) {
            const textStr = text as string;
            if (!this.containsWarning(textStr)) {
              cleanedTranslations[lang] = textStr;
              validEntries++;
            } else {
              hasWarnings = true;
            }
          }
          if (Object.keys(cleanedTranslations).length > 0) {
            cleanedCache[key] = cleanedTranslations;
          }
        }
        
        this.cache = cleanedCache;
        
        // Save cleaned cache if warnings were found
        if (hasWarnings) {
          this.saveCache();
          console.warn(`Removed warning messages from translation cache. Kept ${validEntries} valid translations.`);
        }
      }
    } catch (error) {
      console.warn('Failed to load translation cache:', error);
      // If cache is corrupted, clear it
      this.cache = {};
      localStorage.removeItem('translation_cache');
    }
  }

  /**
   * Save cache to localStorage
   */
  private saveCache() {
    try {
      // Limit cache size to prevent localStorage overflow
      const cacheSize = JSON.stringify(this.cache).length;
      if (cacheSize > 4 * 1024 * 1024) { // 4MB limit
        // Clear oldest entries (simple approach: clear all and rebuild)
        this.cache = {};
      }
      
      localStorage.setItem('translation_cache', JSON.stringify(this.cache));
    } catch (error) {
      console.warn('Failed to save translation cache:', error);
    }
  }

  /**
   * Clear translation cache
   */
  clearCache() {
    this.cache = {};
    localStorage.removeItem('translation_cache');
    console.log('Translation cache cleared');
  }

  /**
   * Clean cache of warning messages (can be called manually)
   */
  cleanCache() {
    this.loadCache(); // This will automatically clean warnings
  }

  /**
   * Clear rate limit (allows immediate retry)
   * Use this if you want to manually reset the rate limit
   */
  clearRateLimit() {
    this.rateLimited = false;
    this.rateLimitUntil = 0;
    localStorage.removeItem('translation_rate_limit');
    console.log('Translation rate limit cleared. API calls will resume.');
  }

  /**
   * Get rate limit status
   */
  getRateLimitStatus(): { isLimited: boolean; minutesRemaining: number } {
    if (this.isRateLimited()) {
      const minutesRemaining = Math.round((this.rateLimitUntil - Date.now()) / 60000);
      return { isLimited: true, minutesRemaining: Math.max(0, minutesRemaining) };
    }
    return { isLimited: false, minutesRemaining: 0 };
  }

  /**
   * Get supported languages
   */
  getSupportedLanguages(): { code: string; name: string }[] {
    return [
      { code: 'en', name: 'English' },
      { code: 'fr', name: 'Français' },
      { code: 'sw', name: 'Kiswahili' },
      { code: 'es', name: 'Español' },
      { code: 'pt', name: 'Português' },
      { code: 'ar', name: 'العربية' },
      { code: 'zh', name: '中文' },
      { code: 'hi', name: 'हिन्दी' },
      { code: 'de', name: 'Deutsch' },
      { code: 'it', name: 'Italiano' },
      { code: 'ja', name: '日本語' },
      { code: 'ko', name: '한국어' },
      { code: 'ru', name: 'Русский' },
      { code: 'tr', name: 'Türkçe' },
      { code: 'vi', name: 'Tiếng Việt' },
      { code: 'nl', name: 'Nederlands' },
      { code: 'pl', name: 'Polski' },
      { code: 'th', name: 'ไทย' },
      { code: 'uk', name: 'Українська' },
    ];
  }

  /**
   * Configure to use Google Translate API
   */
  configureGoogleTranslate(apiKey: string) {
    this.apiKey = apiKey;
    this.useGoogleTranslate = true;
  }
}

// Export singleton instance
export const translationService = new TranslationService();

